<template>
  <div :class="bannerClasses">
    <div
      v-for="banner in bannersArray"
      :key="banner.bannerText"
    >
      <ItemTableActionBarHeaderMessage
        v-if="banner.show()"
        :href="$options.adJoinedWindowsDevicesKbLink"
        level="warn"
        linkText="Learn More."
        :message="banner.bannerText"
        :setLoadingOverlay="setLoadingOverlay"
      />
    </div>
  </div>
</template>

<script>
import {
  ItemTypeConsts, ItemTypeRoutes, StoreModelMap, StoreModuleMap,
} from 'client/util/Constants';
import { mapGetters } from 'vuex';
import ItemTableActionBarHeaderMessage from 'client/components/ItemTable/ItemTableActionBarHeaderMessage.vue';
import KbArticleUrls from 'util/Constants/KbArticleUrls';
import PageNavigationUtil from 'client/util/PageNavigationUtil';

const systemCollection = StoreModuleMap[ItemTypeConsts.system];
const systemModel = StoreModelMap[ItemTypeConsts.device];

export default {
  name: 'ItemTableActionBarMessageList',

  components: {
    ItemTableActionBarHeaderMessage,
  },

  props: {
    inDetailsAsidePanel: {
      type: Boolean,
      required: true,
    },
    itemType: {
      type: String,
      required: true,
    },
    setLoadingOverlay: {
      type: Function,
      required: true,
    },
  },

  adJoinedWindowsDevicesKbLink: KbArticleUrls.ADJoinedWindowsDevicesSupportKB,

  computed: {
    ...mapGetters(systemCollection, [
      'isADJoinedDeviceSelected',
      'isAzureADJoinedDeviceSelected',
    ]),

    ...mapGetters(systemModel, [
      'isWindowsDomainJoined',
      'isAzureDomainJoined',
    ]),

    ADJoinedBindingBannerText() {
      return this.itemType === ItemTypeConsts.policy
        ? 'You are binding a policy to an AD domain joined device. This may result in unexpected behavior.'
        : 'Binding a user to this AD domain joined device is not supported.';
    },

    asideBindingToADJoinedBannerText() {
      const locationHash = PageNavigationUtil.getLocationHash();
      const inPolicyAside = locationHash.includes(ItemTypeRoutes[ItemTypeConsts.configuration])
      && locationHash.includes('/details');

      return inPolicyAside
        ? 'You are binding a policy to an AD domain joined device. This may result in unexpected behavior.'
        : 'AD Domain Joined device(s) are selected in the table below.';
    },

    bannerClasses() {
      return this.bannersArray.some(banner => banner.show()) ? this.$style.deviceInfoBanner : '';
    },

    bannersArray() {
      return [
        {
          show: () => this.showADJoinedDevicesBanner,
          bannerText: this.asideBindingToADJoinedBannerText,
        },
        {
          show: () => this.showBindingToADJoinedDeviceBanner,
          bannerText: this.ADJoinedBindingBannerText,
        },
        {
          show: () => this.showBindingUserToAzureADJoinedDeviceBanner,
          bannerText: 'Binding a user to an Azure AD joined device may result in unexpected behavior.',
        },
        {
          show: () => this.showBindingPolicyToAzureADJoinedDeviceBanner,
          bannerText: 'Binding a policy to an Azure AD joined device may result in unexpected behavior.',
        },
      ];
    },

    // Controls showing the warning banner in the devices tab inside of the users or policies asides
    showADJoinedDevicesBanner() {
      const inPolicyAside = PageNavigationUtil.getLocationHash()
        .includes(ItemTypeRoutes[ItemTypeConsts.configuration]);
      const inUsersAside = PageNavigationUtil.getLocationHash()
        .includes(ItemTypeRoutes[ItemTypeConsts.systemUser]);
      const isDeviceTab = this.itemType === ItemTypeConsts.device;

      return this.inDetailsAsidePanel
        && (inPolicyAside || inUsersAside)
        && isDeviceTab
        && this.isADJoinedDeviceSelected;
    },

    // Controls showing the Azure AD warning banner inside the:
    //  devices aside->policy tab
    //  policies aside->device tab
    showBindingPolicyToAzureADJoinedDeviceBanner() {
      const inPoliciesAside = PageNavigationUtil.getLocationHash()
        .includes(ItemTypeRoutes[ItemTypeConsts.configuration]);
      const inDevicesAside = PageNavigationUtil.getLocationHash()
        .includes(ItemTypeRoutes[ItemTypeConsts.device]);

      const isPolicyTab = this.itemType === ItemTypeConsts.policy;
      const isDeviceTab = this.itemType === ItemTypeConsts.device;
      return this.inDetailsAsidePanel
        && ((inDevicesAside && isPolicyTab && this.isAzureDomainJoined)
          || (inPoliciesAside && isDeviceTab && this.isAzureADJoinedDeviceSelected));
    },

    // Controls showing the AD warning banner in the devices aside
    // inside the users or policies tabs
    showBindingToADJoinedDeviceBanner() {
      const isUserOrPolicyTab = [ItemTypeConsts.systemUser, ItemTypeConsts.policy]
        .includes(this.itemType);

      return this.inDetailsAsidePanel
        && isUserOrPolicyTab
        && this.isWindowsDomainJoined;
    },

    // Controls showing the Azure AD warning banner inside the:
    //  devices aside->user tab
    //  users aside->device tab
    showBindingUserToAzureADJoinedDeviceBanner() {
      const inUsersAside = PageNavigationUtil.getLocationHash()
        .includes(ItemTypeRoutes[ItemTypeConsts.systemUser]);
      const inDevicesAside = PageNavigationUtil.getLocationHash()
        .includes(ItemTypeRoutes[ItemTypeConsts.device]);

      const isUserTab = this.itemType === ItemTypeConsts.systemUser;
      const isDeviceTab = this.itemType === ItemTypeConsts.device;
      return this.inDetailsAsidePanel
        && ((inDevicesAside && isUserTab && this.isAzureDomainJoined)
          || (inUsersAside && isDeviceTab && this.isAzureADJoinedDeviceSelected));
    },
  },
};
</script>

<style scoped module>

.deviceInfoBanner {
  padding: 20px 20px 0;
}
</style>
